import * as Sentry from "@sentry/react";
import {
  PaymentElement,
  useElements,
  useStripe,
  CardElement,
} from "@stripe/react-stripe-js";
import { installmentScheduleTypeConstants } from "constants/account";
import { orderTypeConstants, orderStatusConstants } from "constants/order";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import dayjs from "dayjs";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Label } from "layout/typography/Label";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { useContext, useEffect, useMemo, useState } from "react";
import { useGetCustomsByOrderTypeAndAccountIdQuery } from "store/queries/customize";
import { useGetCurrentLegalDocumentsQuery } from "store/queries/legalDocuments";
import {
  useCreatePaymentMutation,
  useCreateSplitPaymentIntentMutation,
  useCaptureSplitPaymentMutation,
  useCancelPaymentIntentsMutation,
  useGetSavedPaymentMethodQuery,
} from "store/queries/order";
import { useAttachPaymentMethodMutation } from "store/queries/stripe";
import { ICreatePaymentTermsAcceptanceDto } from "types/LegalDocuments/LegalDocuments";
import { IOrder } from "types/Order/Order";
import { TermsAcceptanceContext } from "types/TermsAcceptance/TermsAcceptance";

import { CardAccordion } from "./components/CardAccordion";
import { HEADER_PANEL_TEXT, HeaderPanel } from "./components/HeaderPanel";
import { LegalDocumentDialog } from "./components/LegalDocumentDialog";
import { PaymentSplitSection } from "./components/PaymentSplitSection";
import { SavedCard } from "./components/SavedCard";
import {
  StyledButton,
  ContainerForm,
  EmailContainer,
  StyledInfoContainer,
  StyledInputNumber,
  StyledSplitButton,
} from "./styled";

const SECURE_TRANSACTIONS_TEXT = "All transactions are secure and encrypted";

const PAYMENT_PLAN_AUTHORIZATION_TEXT =
  "I authorize Clear Health to automatically charge the provided credit card for the specified (and updated if applicable) amount in the Payment Plan for each billing period";

// Helper function to standardize payment types
const standardizePaymentType = (paymentType: string): string => {
  const cardBrands = [
    "visa",
    "mastercard",
    "amex",
    "discover",
    "jcb",
    "diners",
    "unionpay",
  ];

  // If the payment type is a card brand, standardize to "Card"
  if (cardBrands.includes(paymentType.toLowerCase())) {
    return "Card";
  }

  // Handle specific payment method types from Stripe that need conversion
  switch (paymentType.toLowerCase()) {
    case "card":
      return "Card";
    case "apple_pay":
      return "ApplePay";
    case "google_pay":
      return "GooglePay";
    default:
      // For unknown payment types, just return as is with first letter capitalized
      return paymentType.charAt(0).toUpperCase() + paymentType.slice(1);
  }
};

type PaymentSectionProps = {
  prices: {
    clearPrice: number;
    totalSaving: number;
    fullPrice: number;
  };
  order: IOrder;
  expirationDays: number;
  onReturnClick?: () => void;
  showPaymentSection: {
    paymentPlan: boolean;
    paymentSection: boolean;
    paymentSuccess: boolean;
  };
  setShowPaymentSection: any;
  payRemainingPrice?: boolean;
  createOrderPaymentIntent?: any;
  stripeCustomerId?: string;
  onUpdateOrder?: () => void;
  isDownPaymentOnly?: boolean;
};

export function PaymentSection({
  order,
  prices,
  onReturnClick,
  expirationDays,
  showPaymentSection,
  setShowPaymentSection,
  payRemainingPrice,
  createOrderPaymentIntent,
  stripeCustomerId,
  onUpdateOrder,
  isDownPaymentOnly,
}: PaymentSectionProps) {
  useDocumentTitle("Payment Page");
  const { userInfo } = useUserWithAccount();

  const { pageViewEvents } = useAnalytics();

  const { data: contentData } = useGetCustomsByOrderTypeAndAccountIdQuery(
    { orderType: order.orderType, accountId: order.account.id },
    {
      skip: !order,
    }
  );

  const { overviewPage: overviewContent } = contentData?.data || {};

  const [createPayment] = useCreatePaymentMutation();
  const [createSplitPaymentIntent] = useCreateSplitPaymentIntentMutation();
  const [captureSplitPayment] = useCaptureSplitPaymentMutation();
  const [cancelPaymentIntents] = useCancelPaymentIntentsMutation();

  const [attachPaymentMethodToCustomer, { isError: isAttachedPaymentError }] =
    useAttachPaymentMethodMutation();

  const { current: toastElement } = useContext(ToastContext);

  const [isProcessing, setIsProcessing] = useState(false);

  const [patientEmail, setPatientEmail] = useState(order.patient.email);
  const [emailValid, setEmailValid] = useState(true);
  const [cardInformationComplete, setCardInformationComplete] = useState(false);

  // State for split payment
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [secondCardAmount, setSecondCardAmount] = useState<number>(0);
  const [firstCardAmount, setFirstCardAmount] = useState<number>(
    prices.clearPrice
  );
  const [secondCardInformationComplete, setSecondCardInformationComplete] =
    useState(false);
  // State for tracking which payment method should be used for future payments
  const [preferredPaymentMethodIndex, setPreferredPaymentMethodIndex] =
    useState<number>(0); // 0 = saved card, 1 = new card

  const [editableRemainingAmount, setEditableRemainingAmount] =
    useState<number>(prices.clearPrice);

  const elements = useElements();
  const stripe = useStripe();

  const [savedPaymentMethod, setSavedPaymentMethod] = useState<any>(null);
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openLegalDocument, setOpenLegalDocument] = useState<
    "terms" | "privacy" | null
  >(null);

  const { data: legalDocuments, isLoading: isLegalDocumentsFetching } =
    useGetCurrentLegalDocumentsQuery();
  // Query for saved payment method when payRemainingPrice is true
  const {
    data: savedPaymentMethodData,
    isLoading: isSavedPaymentMethodLoading,
  } = useGetSavedPaymentMethodQuery(order.id, {
    skip: !payRemainingPrice,
  });

  // Set saved payment method when data is loaded
  useEffect(() => {
    if (savedPaymentMethodData?.data && payRemainingPrice) {
      setSavedPaymentMethod(savedPaymentMethodData.data);
      setUseSavedPaymentMethod(true);
    }
  }, [savedPaymentMethodData, payRemainingPrice]);

  // useEffect(() => {
  //   if (!patientEmail || patientEmail === "") {
  //     setEmailValid(true);
  //     return;
  //   }
  //   setEmailValid(isValidEmail(patientEmail));
  // }, [patientEmail]);

  const activePaymentPlan = useMemo(() => {
    return order?.paymentPlan?.find((plan) => plan?.active);
  }, [order]);

  const estimatedCost = useMemo(() => {
    const orderTypeAmountMap = new Map<string, number>([
      [orderTypeConstants.BARIATRICS, prices.clearPrice],
      [orderTypeConstants.BUNDLED, prices.clearPrice],
      [orderTypeConstants.PATIENT_RESPONSIBILITY, prices.fullPrice],
      [orderTypeConstants.GFE, prices.fullPrice],
      [orderTypeConstants.ED, prices.clearPrice],
    ]);
    const estimatedCost = orderTypeAmountMap.get(order.orderType) || 0;
    return estimatedCost;
  }, [prices, order]);

  const initialPayment = useMemo(() => {
    const minDownPaymentPercent =
      (activePaymentPlan?.paymentPlanMinDownPaymentPercent ?? 0) || 0;
    const minDownPaymentFlat = activePaymentPlan?.paymentPlanMinDownPaymentFlat;

    const minDownPayment =
      minDownPaymentFlat || estimatedCost * minDownPaymentPercent;

    return minDownPayment;
  }, [prices, order, activePaymentPlan, estimatedCost]);

  const paymentPlanDuration = useMemo(() => {
    const activePaymentPlan = order?.paymentPlan?.find(
      (plan: any) => plan.active
    );
    return activePaymentPlan?.paymentPlanDuration;
  }, [order]);

  const monthlyPaymentForPaymentPlan = useMemo(() => {
    if (!paymentPlanDuration) return 0;
    const remainingAmount = estimatedCost - centsToDollars(initialPayment);
    return Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
  }, [estimatedCost, initialPayment, paymentPlanDuration]);

  const nextInstallmentDueDate = useMemo(() => {
    const { installmentScheduleType } = order.account;
    const isTwoWeekSchedule =
      installmentScheduleType ===
      installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;
    let startDate: dayjs.Dayjs;
    let addAMonth = true;
    if (isTwoWeekSchedule) {
      const twoWeeksBefore = dayjs
        .utc(order.dateOfService)
        .subtract(14, "days");
      const now = dayjs.utc();

      startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
      addAMonth = !!twoWeeksBefore.isBefore(now);
    } else {
      startDate = dayjs();
    }

    const installments = Array.from(
      { length: paymentPlanDuration || 0 },
      (_, index) => {
        const updatedIndex = addAMonth ? index + 1 : index;
        const date = startDate.add(updatedIndex, "month").format("MM/DD/YYYY");

        return { date };
      }
    );
    return installments[0]?.date;
  }, [order, paymentPlanDuration]);

  // Get the total amount to be paid based on payment type
  const totalPaymentAmount = useMemo(() => {
    if (showPaymentSection.paymentPlan) {
      return centsToDollars(initialPayment);
    }
    return prices.clearPrice;
  }, [showPaymentSection.paymentPlan, initialPayment, prices.clearPrice]);

  // Update the useEffect for first card amount calculation
  useEffect(() => {
    if (isSplitPayment) {
      setFirstCardAmount(totalPaymentAmount - secondCardAmount);
    } else {
      setFirstCardAmount(totalPaymentAmount);
    }
  }, [secondCardAmount, totalPaymentAmount, isSplitPayment]);

  // Update the handleAddSecondCard function
  const handleAddSecondCard = () => {
    setIsSplitPayment(true);
    const secondCardAmount = Math.floor(totalPaymentAmount / 2);
    setSecondCardAmount(secondCardAmount);
    setFirstCardAmount(totalPaymentAmount - secondCardAmount);
  };

  // Update the handleRemoveSecondCard function
  const handleRemoveSecondCard = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSplitPayment(false);
    setSecondCardAmount(0);
    setFirstCardAmount(totalPaymentAmount);
  };

  // Payment Split: changing first card amount auto-balances second
  const handlePaymentSplitFirstCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(totalPaymentAmount - clamped);
  };

  // Payment Split: changing second card amount auto-balances first (via useEffect)
  const handlePaymentSplitSecondCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(clamped);
  };

  // Modify payNowDisabled to account for split payment
  const payNowDisabled = useMemo(() => {
    if (!termsAccepted) {
      return true;
    }
    // If using saved payment method, only email validation is needed
    if (payRemainingPrice && useSavedPaymentMethod && savedPaymentMethod) {
      return !emailValid;
    }

    if (isSplitPayment) {
      return (
        !cardInformationComplete ||
        !secondCardInformationComplete ||
        !emailValid ||
        secondCardAmount <= 0 ||
        secondCardAmount >= totalPaymentAmount
      );
    }
    return !cardInformationComplete || !emailValid;
  }, [
    cardInformationComplete,
    secondCardInformationComplete,
    emailValid,
    isSplitPayment,
    secondCardAmount,
    totalPaymentAmount,
    payRemainingPrice,
    useSavedPaymentMethod,
    savedPaymentMethod,
    termsAccepted,
  ]);

  const handleAmountChange = (e: any): void => {
    const value = parseFloat(e.target.value);
    if (!Number.isNaN(value) && value >= 1 && value <= prices.clearPrice) {
      setEditableRemainingAmount(value);
    }
    if (Number.isNaN(value)) {
      setEditableRemainingAmount(1);
    }
  };
  const paymentSectionHeaderProps = useMemo(() => {
    if (showPaymentSection.paymentSection) {
      return {
        title: "Checkout",
        amount: prices.clearPrice,
      };
    }
    if (payRemainingPrice) {
      return {
        title: "Remaining amount",
        amount: editableRemainingAmount,
      };
    }
    if (showPaymentSection.paymentPlan) {
      return {
        title: activePaymentPlan?.isDownPaymentOnly
          ? "Down Payment"
          : "Initial Payment",
        amount: centsToDollars(initialPayment),
      };
    }
    return null;
  }, [
    showPaymentSection,
    prices,
    payRemainingPrice,
    initialPayment,
    editableRemainingAmount,
  ]);

  const debouncedPageViewEvents = debounce((order, event: string) => {
    pageViewEvents(
      {
        accountName: order?.accountName,
        orderType: order?.orderType,
        communicationMethod: order?.communicationMethod,
      },
      event
    );
  }, 500);

  const handleSplitPayment = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    if (!stripe || !elements) return false;

    try {
      // Get client secrets for both cards
      const splitPaymentResponse = await createSplitPaymentIntent({
        orderId: order.externalId || "",
        amounts: [
          Math.round(firstCardAmount * 100),
          Math.round(secondCardAmount * 100),
        ],
        paymentPlan: showPaymentSection.paymentPlan,
        payRemainingPrice: !!payRemainingPrice,
        manualCapture: true,
      }).unwrap();

      if (!splitPaymentResponse.success) {
        toastElement?.show({
          severity: "error",
          detail: "Failed to create split payment",
          summary: "Payment setup failed",
          life: 6000,
        });
        return false;
      }

      // Store payment intent IDs from backend response
      const paymentIntentIds = splitPaymentResponse.data.paymentIntentIds || [];

      // Get payment elements for both cards
      const firstCardElement = elements.getElement(PaymentElement);
      const secondCardElement = elements.getElement(CardElement);

      if (!firstCardElement || !secondCardElement) {
        toastElement?.show({
          severity: "error",
          detail: "Card information is missing",
          summary: "Payment failed",
          life: 6000,
        });
        return false;
      }

      // Submit elements before confirming payment
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toastElement?.show({
          severity: "error",
          detail: submitError.message,
          summary: "Card validation failed",
          life: 6000,
        });
        return false;
      }

      // Process first card payment with confirmPayment
      let firstCardResult;
      let firstCardError = false;
      let errorMessage = "";

      try {
        firstCardResult = await stripe.confirmPayment({
          elements,
          clientSecret: splitPaymentResponse.data.clientSecrets[0],
          redirect: "if_required",
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: `${order.patient.firstName} ${order.patient.lastName}`,
                email: patientEmail,
              },
            },
          },
        });

        // Check for error in the result
        if (firstCardResult && "error" in firstCardResult) {
          firstCardError = true;
          errorMessage = firstCardResult.error?.message || "Card was declined";
        }
      } catch (error) {
        // Handle any exceptions during confirmPayment
        firstCardError = true;
        errorMessage = "An unexpected error occurred with your card";
      }

      if (firstCardError || !firstCardResult) {
        toastElement?.show({
          severity: "error",
          detail: errorMessage || "Unable to process your card",
          summary: "First card payment failed",
          life: 6000,
        });
        if (paymentIntentIds.length > 0) {
          try {
            await cancelPaymentIntents({
              orderId: order.id,
              paymentIntentIds,
              failureReason: "First card failed",
              userId: userInfo?.id,
            });
          } catch (error) {
            console.error("Failed to cancel payment intents:", error);
          }
        }

        setIsProcessing(false);
        return false;
      }

      // Process second card payment with manual capture
      const secondCardResult = await stripe.confirmCardPayment(
        splitPaymentResponse.data.clientSecrets[1],
        {
          payment_method: {
            card: secondCardElement,
            billing_details: {
              name: `${order.patient.firstName} ${order.patient.lastName}`,
              email: patientEmail,
            },
          },
          setup_future_usage: "off_session",
        }
      );

      // Check for second card error
      if (secondCardResult && "error" in secondCardResult) {
        toastElement?.show({
          severity: "error",
          detail: secondCardResult.error?.message || "Card was declined",
          summary: "Second card payment failed",
          life: 6000,
        });
        if (paymentIntentIds.length > 0) {
          try {
            await cancelPaymentIntents({
              orderId: order.id,
              paymentIntentIds,
              failureReason: "Second card failed",
              userId: userInfo?.id,
            });
          } catch (error) {
            console.error("Failed to cancel payment intents:", error);
          }
        }

        setIsProcessing(false);
        return false;
      }

      // Get payment intents from successful responses
      const firstPaymentIntent =
        firstCardResult && "paymentIntent" in firstCardResult
          ? firstCardResult.paymentIntent
          : null;

      const secondPaymentIntent =
        secondCardResult && "paymentIntent" in secondCardResult
          ? secondCardResult.paymentIntent
          : null;

      // We should have both payment intents at this point
      if (!firstPaymentIntent || !secondPaymentIntent) {
        toastElement?.show({
          severity: "error",
          summary: "Payment Processing Error",
          detail: "Could not process both cards. Please try again.",
          life: 6000,
        });

        // Cancel payment intents if we can't complete the process
        if (paymentIntentIds.length > 0) {
          try {
            await cancelPaymentIntents({
              orderId: order.id,
              paymentIntentIds,
              failureReason: "Missing payment intent data",
              userId: userInfo?.id,
            });
          } catch (error) {
            // Error handled silently
          }
        }

        setIsProcessing(false);
        return false;
      }

      // Capture request
      const captureResponse = await captureSplitPayment({
        orderId: order.id,
        paymentIntentIds: [firstPaymentIntent.id, secondPaymentIntent.id],
        amounts: [
          Math.round(firstCardAmount * 100),
          Math.round(secondCardAmount * 100),
        ],
        paymentMethodIds: [
          firstPaymentIntent.payment_method as string,
          secondPaymentIntent.payment_method as string,
        ],
        paymentTypes: [
          firstPaymentIntent.payment_method_types[0],
          secondPaymentIntent.payment_method_types[0],
        ],
        userId: userInfo?.id,
        paymentPlan: showPaymentSection.paymentPlan,
        preferredPaymentMethodIndex,
        termsAcceptance,
      }).unwrap();

      if (!captureResponse.success) {
        toastElement?.show({
          severity: "warn",
          summary: "Payment Processing Issue",
          detail:
            captureResponse.message ||
            "There was an issue processing your payment. Please contact support for assistance.",
          life: 6000,
        });
        return false;
      }

      // We need to attach the preferred payment method to the customer for autopayments
      if (showPaymentSection.paymentPlan) {
        const preferredPaymentMethodId = [
          firstPaymentIntent.payment_method as string,
          secondPaymentIntent.payment_method as string,
        ][preferredPaymentMethodIndex];

        attachPaymentMethodToCustomer({
          paymentMethodId: preferredPaymentMethodId,
          stripeCustomerId: splitPaymentResponse.data.stripeCustomerId,
        })
          .then(() => {
            // Successfully attached payment method
          })
          .catch(() => {
            // toastElement?.show({
            //   severity: "warn",
            //   summary: "Payment Method Attachment Failed",
            //   detail:
            //     "We encountered a challenge while trying to save your payment method. This may affect future automatic payments. Please contact support for assistance.",
            //   life: 6000,
            // });
            console.error(
              "Payment method attachment failed - but may have succeeded in Stripe"
            );
          });
      }

      return true;
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Payment Processing Failed",
        detail: "An unexpected error occurred. Please try again later.",
        life: 6000,
      });
      return false;
    }
  };

  const handleSuccessfulPayment = async (paymentData: {
    orderId: string;
    amountInCents: number;
    paymentType: string;
    stripeBalanceTransactionId?: string;
    userId?: string;
    paymentMethodId?: string;
    paymentPlan?: boolean;
    payRemainingPrice?: boolean;
    savePaymentMethod?: boolean;
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto;
  }) => {
    pageViewEvents(
      {
        accountName: order.account.name,
        orderType: order.orderType,
        communicationMethod: order.patient.preferredContactMethod,
      },
      events.PURCHASE
    );

    const requestStartTime = Date.now();
    try {
      await createPayment(paymentData).unwrap();
      if (onUpdateOrder) {
        onUpdateOrder();
      }
      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: false,
        paymentSuccess: true,
        receiptAvailable: true,
      });
    } catch (error: any) {
      const requestDurationMs = Date.now() - requestStartTime;
      const { connection } = navigator as any;

      Sentry.captureMessage(
        "Payment record creation failed after Stripe charge",
        {
          level: "fatal",
          tags: {
            operation: "create_payment_frontend",
            orderId: paymentData.orderId,
            errorType: error?.status || "unknown",
            browserOnline: String(navigator.onLine),
          },
          extra: {
            orderId: paymentData.orderId,
            stripeBalanceTransactionId: paymentData.stripeBalanceTransactionId,
            amountInCents: paymentData.amountInCents,
            paymentType: paymentData.paymentType,
            paymentPlan: paymentData.paymentPlan,
            payRemainingPrice: paymentData.payRemainingPrice,
            httpStatus: error?.status || error?.data?.status,
            errorMessage:
              error?.data?.messages ||
              error?.data?.message ||
              error?.message ||
              "Unknown error",
            rawError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
            requestDurationMs,
            navigatorOnline: navigator.onLine,
            connectionType: connection?.effectiveType || "unknown",
            connectionDownlink: connection?.downlink,
            connectionRtt: connection?.rtt,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
        }
      );
      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: false,
        paymentSuccess: true,
        receiptAvailable: false,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const finishPayment = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    if (!stripe || !elements) return;
    await elements.submit();

    // We need to store new client secret if we create a new payment intent
    let newClientSecret: string | null = null;
    let paymentStripeCustomerId: string | null = null;

    if (payRemainingPrice) {
      try {
        // For payRemainingPrice, always create a new payment intent
        // we can't use initial payment intent as this is a custom case
        const response: any = await createOrderPaymentIntent({
          orderId: order.externalId || "",
          paymentPlan: false,
          payRemainingPrice: true,
          customAmount: Math.round(editableRemainingAmount * 100),
          // Only save payment method when using a custom amount and a different card
          // And users wants to save payment method
          savePaymentMethod:
            preferredPaymentMethodIndex === 1 &&
            editableRemainingAmount !== prices.clearPrice,
          // For this payment we want to use the customer's default payment method
          // Only if user wants to use it
          useSavedPaymentMethod,
        }).unwrap();

        // Store new client secret and stripeCustomerId
        if (response && response.data?.client_secret) {
          newClientSecret = response.data.client_secret;
          paymentStripeCustomerId = response.stripeCustomerId;

          // Update elements with new client secret
          await elements.fetchUpdates();
        } else {
          setIsProcessing(false);
          toastElement?.show({
            severity: "error",
            detail: "Failed to create payment intent",
            summary: "Payment failed",
            life: 6000,
          });
          return;
        }

        // If using saved payment method
        if (useSavedPaymentMethod && savedPaymentMethod) {
          // For saved payment methods with direct charge, we don't need to confirm the payment
          // The backend will handle the charge directly
          // We just need to create a record of the payment
          await handleSuccessfulPayment({
            orderId: order.id,
            amountInCents: Math.round(editableRemainingAmount * 100),
            stripeBalanceTransactionId: response.balanceTransactionId || "",
            paymentType: standardizePaymentType(
              savedPaymentMethod.type ||
                savedPaymentMethod.cardDetails?.brand ||
                "card"
            ),
            userId: userInfo?.id,
            paymentMethodId:
              savedPaymentMethod.paymentMethodId || savedPaymentMethod.id,
            payRemainingPrice: true,
            savePaymentMethod: true,
            termsAcceptance,
          });
          return;
        }
      } catch (error: any) {
        setIsProcessing(false);
        // We need to handle specific error messages from backend for saved payment method failures
        // for example when there are no sufficient funds on the card
        if (error?.data?.messages && error.data.messages.length > 0) {
          toastElement?.show({
            severity: "error",
            detail: error.data.messages[0],
            summary: "Payment failed",
            life: 6000,
          });
        } else {
          toastElement?.show({
            severity: "error",
            detail: "There was an error processing your payment",
            summary: "Payment failed",
            life: 6000,
          });
        }
        return;
      }
    }

    if (isSplitPayment && !payRemainingPrice) {
      // Process split payment
      const success = await handleSplitPayment(termsAcceptance);

      if (success) {
        pageViewEvents(
          {
            accountName: order.account.name,
            orderType: order.orderType,
            communicationMethod: order.patient.preferredContactMethod,
          },
          events.PURCHASE
        );

        setShowPaymentSection({
          paymentPlan: false,
          paymentSection: false,
          paymentSuccess: true,
        });
      } else {
        setIsProcessing(false);
      }
    } else {
      // Original single payment flow
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        // If we have a new client secret from a custom amount, we use it
        ...(newClientSecret ? { clientSecret: newClientSecret } : {}),
      });

      if (error) {
        setIsProcessing(false);
        toastElement?.show({
          severity: "error",
          detail: error.message,
          summary: "Payment failed",
          life: 6000,
        });
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // For remaining payments with a different card and custom amount, handle future payment preferences first
        if (
          payRemainingPrice &&
          savedPaymentMethod &&
          !useSavedPaymentMethod &&
          editableRemainingAmount !== prices.clearPrice
        ) {
          // If preferredPaymentMethodIndex = 1, the new card should be used for future payments
          if (
            preferredPaymentMethodIndex === 1 &&
            paymentIntent.payment_method
          ) {
            try {
              await attachPaymentMethodToCustomer({
                paymentMethodId: paymentIntent.payment_method as string,
                stripeCustomerId: paymentStripeCustomerId,
              }).unwrap();
            } catch (error) {
              console.error("Error attaching payment method:", error);
              // toastElement?.show({
              //   severity: "warn",
              //   summary: "Payment Method Attachment Failed",
              //   detail:
              //     "We encountered a challenge while trying to save your payment method for future payments. Please contact support for assistance.",
              //   life: 6000,
              // });
            }
          }
        }

        await handleSuccessfulPayment({
          orderId: order.id,
          amountInCents: paymentIntent.amount,
          stripeBalanceTransactionId: paymentIntent.id,
          paymentType: standardizePaymentType(
            paymentIntent.payment_method_types[0]
          ),
          userId: userInfo?.id,
          paymentMethodId: paymentIntent?.payment_method as string,
          payRemainingPrice,
          ...(payRemainingPrice && preferredPaymentMethodIndex === 1
            ? { savePaymentMethod: true }
            : {}),
          termsAcceptance,
        });
      }
    }
  };

  const finishInitialPayment = async (
    termsAcceptance?: ICreatePaymentTermsAcceptanceDto
  ) => {
    if (!stripe || !elements) return;

    setIsProcessing(true);

    if (isSplitPayment) {
      // Process split payment for initial payment
      const success = await handleSplitPayment(termsAcceptance);

      if (success) {
        pageViewEvents(
          {
            accountName: order.account.name,
            orderType: order.orderType,
            communicationMethod: order.patient.preferredContactMethod,
          },
          events.PURCHASE
        );

        setShowPaymentSection({
          paymentPlan: false,
          paymentSection: false,
          paymentSuccess: true,
        });
      } else {
        setIsProcessing(false);
      }
    } else {
      // CONFIRM PAYMENT FOR INITIAL PAYMENT
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: `${order.patient.firstName} ${order.patient.lastName}`,
              email: patientEmail,
            },
          },
        },
      });

      if (error) {
        setIsProcessing(false);

        toastElement?.show({
          severity: "error",
          detail: error.message,
          summary: "Payment failed",
          life: 6000,
        });
      }

      pageViewEvents(
        {
          accountName: order.account.name,
          orderType: order.orderType,
          communicationMethod: order.patient.preferredContactMethod,
        },
        events.PURCHASE
      );

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // We will try to attach payment method and still show error if it fails

        await attachPaymentMethodToCustomer({
          paymentMethodId: paymentIntent?.payment_method,
          stripeCustomerId,
        })
          .unwrap()
          .then(() => {
            console.log("Successfully attached payment method");
          })
          .catch((attachError) => {
            console.error("Error attaching payment method:", attachError);
            // toastElement?.show({
            //   severity: "warn",
            //   summary: "Payment Method Attachment Failed",
            //   detail:
            //     "We encountered a challenge while trying to save your payment method. This may affect future automatic payments. Please contact support for assistance.",
            //   life: 6000,
            // });
          });

        // We create the payment record
        await handleSuccessfulPayment({
          orderId: order.id,
          amountInCents: paymentIntent.amount,
          stripeBalanceTransactionId: paymentIntent.id,
          paymentType: standardizePaymentType(
            paymentIntent.payment_method_types[0]
          ),
          userId: userInfo?.id,
          paymentMethodId: paymentIntent?.payment_method as string,
          paymentPlan: true,
          termsAcceptance,
        });
      }
    }
  };

  const buildAcceptanceSnapshot = (
    context: TermsAcceptanceContext
  ): Record<string, unknown> | undefined => {
    const displayedText: Record<string, string> = {
      secureTransactions: SECURE_TRANSACTIONS_TEXT,
    };

    if (paymentSectionHeaderProps) {
      displayedText.header = paymentSectionHeaderProps.title;
      displayedText.amount = parsePrice(
        payRemainingPrice ? prices.clearPrice : paymentSectionHeaderProps.amount
      );
    }

    if (
      context === TermsAcceptanceContext.FullPayment &&
      overviewContent?.discountExpires
    ) {
      displayedText.discountExpires = `Discount expires on ${dayjs
        .utc(order.dateOfService)
        .add(expirationDays, "day")
        .format("MMMM D, YYYY")}`;
    }

    if (context === TermsAcceptanceContext.DownPayment) {
      displayedText.paymentPlanAuthorization = PAYMENT_PLAN_AUTHORIZATION_TEXT;

      if (!activePaymentPlan?.isDownPaymentOnly) {
        displayedText.paymentPlanAdjustment = HEADER_PANEL_TEXT;
        displayedText.paymentPlanInstallments = `${parsePrice(
          monthlyPaymentForPaymentPlan
        )}/mo  ${paymentPlanDuration} payments`;
        displayedText.nextPaymentDue = `Next payment due ${nextInstallmentDueDate}`;
      }
    }

    return { displayedText };
  };

  const buildTermsAcceptance = async (
    context: TermsAcceptanceContext
  ): Promise<ICreatePaymentTermsAcceptanceDto | undefined> => {
    const termsAndConditionsVersionId =
      legalDocuments?.data?.termsAndConditions?.id;
    const privacyPolicyVersionId = legalDocuments?.data?.privacyPolicy?.id;
    if (!termsAndConditionsVersionId || !privacyPolicyVersionId) {
      return undefined;
    }
    let ipAddress: string | undefined;
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      ipAddress = data.ip;
    } catch {
      // IP lookup failed — proceed without it
    }
    return {
      acceptanceContext: context,
      termsAndConditionsVersionId,
      privacyPolicyVersionId,
      acceptanceSnapshot: buildAcceptanceSnapshot(context),
      ipAddress,
      userAgent: navigator.userAgent,
    };
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // If this is a remaining price payment, ensure split payment is disabled
    if (payRemainingPrice && isSplitPayment) {
      setIsSplitPayment(false);
      setSecondCardAmount(0);
      setFirstCardAmount(totalPaymentAmount);
    }

    // Validate editable amount for remaining payments

    if (
      payRemainingPrice &&
      (editableRemainingAmount <= 0 ||
        editableRemainingAmount > prices.clearPrice)
    ) {
      toastElement?.show({
        severity: "error",
        detail: `Payment amount must be between $1 and $${prices.clearPrice}`,
        summary: "Invalid amount",
        life: 6000,
      });
      return;
    }

    setIsProcessing(true);

    const termsAcceptance = await buildTermsAcceptance(
      payRemainingPrice
        ? TermsAcceptanceContext.PayRemaining
        : TermsAcceptanceContext.FullPayment
    );

    debouncedPageViewEvents(
      {
        accountName: order.account.name,
        orderType: order.orderType,
        communicationMethod: order.patient.preferredContactMethod,
      },
      events.CLICK_PURCHASE
    );
    try {
      await finishPayment(termsAcceptance);
    } catch (error) {
      toastElement?.show({
        summary: "Error",
        severity: "error",
        detail: "Your payment cannot be completed. Please try again.",
        life: 6000,
      });
    }
  };
  const handleSubmitPaymentPlan = async (e: any) => {
    e.preventDefault();

    setIsProcessing(true);

    const termsAcceptance = await buildTermsAcceptance(
      TermsAcceptanceContext.DownPayment
    );

    debouncedPageViewEvents(
      {
        accountName: order.account.name,
        orderType: order.orderType,
        communicationMethod: order.patient.preferredContactMethod,
      },
      events.CLICK_PURCHASE
    );

    try {
      await finishInitialPayment(termsAcceptance);
    } catch (error) {
      toastElement?.show({
        summary: "Error",
        severity: "error",
        detail: "Your payment cannot be completed. Please try again.",
        life: 6000,
      });
    }
  };

  const handleOpenPrivacyPolicy = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenLegalDocument("privacy");
  };

  const handleOpenTermsAndConditions = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenLegalDocument("terms");
  };

  const payMoreButtonDisabled = useMemo(() => {
    if (!order) {
      return false;
    }
    const payments = order?.paymentPlanBreakdown?.payments;
    if (!payments || !payments.length) return false;
    if (
      (order.status === orderStatusConstants.PARTIALLY_PAID ||
        order.status === orderStatusConstants.PAID) &&
      order?.paymentPlanBreakdown?.payments?.length
    ) {
      const iniitalDeposit = order.paymentPlanBreakdown.payments[0];
      const paymentsWithoutInitialDeposit =
        order.paymentPlanBreakdown.payments.filter(
          (p: any) => !!p.installmentInDb
        );
      const lastPaidInstallment = paymentsWithoutInitialDeposit.reduce(
        (last: any, p: any) => (p.installmentInDb?.paymentId ? p : last),
        null
      );
      if (lastPaidInstallment) {
        return !lastPaidInstallment.transferredToProviders;
      }
      return !iniitalDeposit.transferredToProviders;
    }
    return false;
  }, [order]);

  const priceToDisplay = useMemo(() => {
    if (payRemainingPrice) {
      return prices.clearPrice;
    }
    return paymentSectionHeaderProps?.amount;
  }, [payRemainingPrice, prices.clearPrice, paymentSectionHeaderProps]);

  return (
    <ContainerForm>
      {showPaymentSection.paymentPlan &&
        !activePaymentPlan?.isDownPaymentOnly && <HeaderPanel />}
      {paymentSectionHeaderProps && (
        <div className="flex flex-column">
          <span className="title">
            <span>{paymentSectionHeaderProps.title}</span>
            {!payRemainingPrice && (
              <span>{parsePrice(paymentSectionHeaderProps.amount)}</span>
            )}
            {payRemainingPrice && <span>{parsePrice(prices.clearPrice)}</span>}
          </span>
        </div>
      )}
      {showPaymentSection.paymentPlan &&
        !activePaymentPlan?.isDownPaymentOnly && (
          <div className="flex flex-column my-3 align-items-center">
            <p className="text-sm font-normal" style={{ color: "#64636E" }}>
              {parsePrice(monthlyPaymentForPaymentPlan)}/mo{"   "}{" "}
              <span className="ml-2">{paymentPlanDuration} payments</span>
            </p>
            <p className="text-sm font-normal" style={{ color: "#64636E" }}>
              Next payment due {nextInstallmentDueDate}
            </p>
          </div>
        )}
      {payRemainingPrice && (
        <div className="flex flex-column mt-4 mb-4">
          <label htmlFor="custom-amount" className="text-sm mb-1">
            Custom Amount:
          </label>
          <StyledInputNumber
            id="custom-amount"
            value={editableRemainingAmount}
            mode="currency"
            currency="USD"
            locale="en-US"
            min={1}
            max={prices.clearPrice}
            onValueChange={handleAmountChange}
          />
        </div>
      )}

      {overviewContent?.discountExpires &&
        !payRemainingPrice &&
        !showPaymentSection.paymentPlan && (
          <span className="mt-1 font-semibold text-base line-height-3 text-center warning">
            Discount expires on{" "}
            {dayjs
              .utc(order.dateOfService)
              .add(expirationDays, "day")
              .format("MMMM D, YYYY")}
          </span>
        )}

      {/* {!payRemainingPrice && (
        <EmailContainer className="flex flex-column mt-5">
          <Label htmlFor="email">Email</Label>
          <div className="relative w-full">
            <InputText
              id="email"
              autoComplete="off"
              data-testid="email-input"
              value={patientEmail || ""}
              className="w-full"
              placeholder="example@email.com"
              onChange={(event) => setPatientEmail(event.target.value)}
            />
            <i className="pi pi-envelope absolute icon" />
          </div>
          {!emailValid && (
            <p className="text-xs text-red-500 font-medium text-left mt-1">
              Email is invalid
            </p>
          )}
        </EmailContainer>
      )} */}

      {/* Saved Payment Method Section */}
      {payRemainingPrice && savedPaymentMethod && (
        <>
          <SavedCard
            savedPaymentMethod={savedPaymentMethod}
            showFooter={
              !useSavedPaymentMethod &&
              editableRemainingAmount !== prices.clearPrice
            }
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            radioValue={0}
          />
          {useSavedPaymentMethod && (
            <Button
              className="p-button-text p-0 mb-4"
              onClick={(e) => {
                e.preventDefault();
                setUseSavedPaymentMethod(!useSavedPaymentMethod);
              }}
            >
              <i className="pi pi-plus-circle mr-2" />
              Use a different card
            </Button>
          )}
        </>
      )}
      {/* Card input section only for pay remaining price - show when not using saved payment method or in addition when using a different card */}
      {payRemainingPrice &&
        !useSavedPaymentMethod &&
        !isSavedPaymentMethodLoading && (
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="New Card Information"
              isFirstCard={false}
              showFooter={
                savedPaymentMethod &&
                editableRemainingAmount !== prices.clearPrice
              }
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={0}
              handleRemoveSecondCard={() => {
                setUseSavedPaymentMethod(!useSavedPaymentMethod);
                setPreferredPaymentMethodIndex(0);
              }}
              footerText="Use this card for future payments"
            >
              <PaymentElement
                onChange={(event) => setCardInformationComplete(event.complete)}
              />
            </CardAccordion>
          </div>
        )}

      {/* First card section */}
      {!payRemainingPrice && (
        <div className="sensitive">
          <CardAccordion
            showStylings={!!isSplitPayment}
            title="First Card"
            isFirstCard
            showFooter={showPaymentSection.paymentPlan && isSplitPayment}
            radioValue={0}
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            cardAmount={firstCardAmount}
          >
            <PaymentElement
              onChange={(event) => setCardInformationComplete(event.complete)}
            />
          </CardAccordion>
        </div>
      )}

      {/* Add/Remove second card button */}
      {!isSplitPayment ? (
        // Hide "Add a second card" button for the Pay Remaining Amount case
        // or when using saved payment method
        !payRemainingPrice && (
          <StyledSplitButton
            // className="p-button-text pl-0"
            onClick={handleAddSecondCard}
            disabled={isProcessing}
          >
            <i
              className="pi pi-plus-circle"
              style={{ marginRight: "4px", marginTop: "2px" }}
            />
            <div className="flex flex-column ml-2 text-left gap-1">
              <span className="font-semibold text-sm">
                Split payment between two cards
              </span>
              <span className="font-normal text-xs">
                Divide your {parsePrice(priceToDisplay || 0)} payment across two
                different payment methods
              </span>
            </div>
          </StyledSplitButton>
        )
      ) : (
        <>
          {/* Second card payment element */}
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="Second Card"
              isFirstCard={false}
              showFooter={showPaymentSection.paymentPlan && isSplitPayment}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={secondCardAmount}
              handleRemoveSecondCard={handleRemoveSecondCard}
              isProcessing={isProcessing}
            >
              <CardElement
                id="second-card-element"
                onChange={(event) =>
                  setSecondCardInformationComplete(event.complete)
                }
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#30313d",
                      "::placeholder": {
                        color: "#757680",
                        fontWeight: 300,
                      },
                    },
                    invalid: {
                      color: "#df1b41",
                    },
                  },
                }}
              />
            </CardAccordion>
          </div>

          <PaymentSplitSection
            firstCardAmount={firstCardAmount}
            secondCardAmount={secondCardAmount}
            totalPaymentAmount={totalPaymentAmount}
            onFirstCardAmountChange={handlePaymentSplitFirstCardChange}
            onSecondCardAmountChange={handlePaymentSplitSecondCardChange}
          />
        </>
      )}

      <div className="flex align-items-start gap-2 mt-2 text-left">
        <Checkbox
          inputId="stripe-payment-terms-acceptance"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.checked ?? false)}
          className="mt-1 flex-shrink-0"
        />
        <label
          htmlFor="stripe-payment-terms-acceptance"
          className="text-sm gray font-normal cursor-pointer m-0 line-height-3"
        >
          {`By clicking "Pay Now", I confirm that I have read and agree to the`}{" "}
          <Button
            type="button"
            className="p-button-text text-sm p-0 border-none"
            onClick={handleOpenPrivacyPolicy}
          >
            Privacy Policy
          </Button>
          {legalDocuments?.data?.privacyPolicy && (
            <>
              {" "}
              (version: {legalDocuments.data.privacyPolicy.version}, effective
              from {parseDate(legalDocuments.data.privacyPolicy.effectiveAt)})
            </>
          )}{" "}
          and{" "}
          <Button
            type="button"
            className="p-button-text text-sm p-0 border-none"
            onClick={handleOpenTermsAndConditions}
          >
            Terms and Conditions
          </Button>
          {legalDocuments?.data?.termsAndConditions && (
            <>
              {" "}
              (version: {legalDocuments.data.termsAndConditions.version},
              effective from{" "}
              {parseDate(
                legalDocuments.data.termsAndConditions.effectiveAt,
                false,
                true
              )}
              )
            </>
          )}
        </label>
      </div>
      {!!showPaymentSection.paymentPlan && (
        <p className="text-sm gray mt-2 text-left font-normal">
          {PAYMENT_PLAN_AUTHORIZATION_TEXT}
        </p>
      )}
      <p className="text-sm gray mt-2 text-center font-normal mt-4">
        <i className="pi pi-lock mr-2 text-sm " />
        {SECURE_TRANSACTIONS_TEXT}
      </p>

      {!!showPaymentSection.paymentPlan && (
        <Button
          type="submit"
          data-testid="button-submit-payment-plan"
          className="w-full justify-content-center  mb-1 mt-2"
          loading={isProcessing}
          onClick={handleSubmitPaymentPlan}
          disabled={payNowDisabled}
        >
          <span className="font-bold">Pay Now</span>
        </Button>
      )}
      {!!showPaymentSection.paymentSection && (
        <StyledButton
          type="submit"
          loading={isProcessing}
          data-testid="button-submit-pay-now"
          className="w-full justify-content-center mt-2"
          onClick={handleSubmit}
          disabled={payNowDisabled}
        >
          <span>Pay Now</span>
        </StyledButton>
      )}

      {!!payRemainingPrice && (
        <Button
          type="submit"
          loading={isProcessing}
          data-testid="button-submit-pay-now"
          className="w-full justify-content-center mt-2"
          onClick={handleSubmit}
          disabled={payNowDisabled || payMoreButtonDisabled}
        >
          <span>
            {payMoreButtonDisabled
              ? "Last transfer is being processed..."
              : "Pay Now"}
          </span>
        </Button>
      )}

      {onReturnClick && (
        <div className="flex align-items-center justify-content-center mt-3">
          <Button
            className="p-button-text mb-0 p-button-secondary p-0"
            onClick={onReturnClick}
          >
            <p className="text-sm purple font-semibold">Back to details</p>
          </Button>
        </div>
      )}
      <div className="relative">
        <img src="/logo_stripe.png" alt="stripe logo" />
      </div>

      <LegalDocumentDialog
        visible={openLegalDocument === "terms"}
        onClose={() => setOpenLegalDocument(null)}
        header="Terms of Use"
        content={legalDocuments?.data?.termsAndConditions?.content}
        isLoading={isLegalDocumentsFetching}
      />
      <LegalDocumentDialog
        visible={openLegalDocument === "privacy"}
        onClose={() => setOpenLegalDocument(null)}
        header="Privacy Policy"
        content={legalDocuments?.data?.privacyPolicy?.content}
        isLoading={isLegalDocumentsFetching}
      />
    </ContainerForm>
  );
}
